import { type FC, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import type { UUID } from '@/index'
import {
  getDisplayHostName,
  getDisplayProjectName,
} from '@/features/events/conversation/state/types'
import type { BlockingInboxEntry } from '../types'
import { getBubbleLabelSx, getBubbleSx } from '../styles'
import { BlockingSessionCard } from './BlockingSessionCard'

interface BlockingSessionTreeProps {
  readonly entries: readonly BlockingInboxEntry[]
  readonly submittingSessionIds: ReadonlySet<UUID>
  readonly onSelect: (sessionId: UUID) => void
  readonly onAllowOnce: (entry: BlockingInboxEntry) => void
}

interface ProjectGroup {
  readonly projectName: string
  readonly entries: readonly BlockingInboxEntry[]
}

interface HostGroup {
  readonly hostName: string
  readonly projects: ProjectGroup[]
}

export const BlockingSessionTree: FC<BlockingSessionTreeProps> = ({
  entries,
  submittingSessionIds,
  onSelect,
  onAllowOnce,
}) => {
  const theme = useTheme()
  const hostGroups = useMemo(() => groupEntries(entries), [entries])

  return (
    <Box sx={treeSx}>
      {hostGroups.map((host) => (
        <Box key={host.hostName} sx={getBubbleSx('outer', theme)}>
          <Typography variant="caption" sx={hostLabelSx(theme)} noWrap>
            {host.hostName}
          </Typography>
          {host.projects.map((project) => (
            <Box key={`${host.hostName}/${project.projectName}`} sx={getBubbleSx('middle', theme)}>
              <Typography variant="caption" sx={projectLabelSx(theme)} noWrap>
                {project.projectName}
              </Typography>
              {project.entries.map((entry) => (
                <BlockingSessionCard
                  key={entry.session.sessionId}
                  entry={entry}
                  isSubmitting={submittingSessionIds.has(entry.session.sessionId)}
                  onSelect={onSelect}
                  onAllowOnce={onAllowOnce}
                />
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}

const compareByRecencyDesc = (a: BlockingInboxEntry, b: BlockingInboxEntry): number =>
  new Date(b.event.timestamp).getTime() - new Date(a.event.timestamp).getTime()

function newestEntryTime(entries: readonly BlockingInboxEntry[]): number {
  if (entries.length === 0) return 0
  return entries.reduce(
    (max, entry) => Math.max(max, new Date(entry.event.timestamp).getTime()),
    -Infinity,
  )
}

function groupEntries(entries: readonly BlockingInboxEntry[]): HostGroup[] {
  const hostMap = new Map<string, Map<string, BlockingInboxEntry[]>>()

  for (const entry of entries) {
    const hostName = getDisplayHostName(entry.session)
    const projectName = getDisplayProjectName(entry.session)

    let projectMap = hostMap.get(hostName)
    if (!projectMap) {
      projectMap = new Map<string, BlockingInboxEntry[]>()
      hostMap.set(hostName, projectMap)
    }

    const projectEntries = projectMap.get(projectName)
    if (projectEntries) {
      projectEntries.push(entry)
    } else {
      projectMap.set(projectName, [entry])
    }
  }

  const hostGroups = Array.from(hostMap.entries()).map(([hostName, projectMap]) => {
    const projects = Array.from(projectMap.entries())
      .map(([projectName, projectEntries]) => ({
        projectName,
        entries: [...projectEntries].sort(compareByRecencyDesc),
      }))
      .sort((a, b) => newestEntryTime(b.entries) - newestEntryTime(a.entries))

    return { hostName, projects }
  })

  return hostGroups.sort((a, b) => newestHostTime(b.projects) - newestHostTime(a.projects))
}

function newestHostTime(projects: readonly ProjectGroup[]): number {
  if (projects.length === 0) return 0
  return projects.reduce(
    (max, project) => Math.max(max, newestEntryTime(project.entries)),
    -Infinity,
  )
}

const treeSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  p: 1.5,
}

const hostLabelSx = (theme: Theme): SxProps<Theme> => ({
  ...getBubbleLabelSx('outer', theme),
  fontSize: '0.8rem',
  fontWeight: 700,
  textTransform: 'none',
  lineHeight: 1.6,
})

const projectLabelSx = (theme: Theme): SxProps<Theme> => ({
  ...getBubbleLabelSx('middle', theme),
  fontSize: '0.875rem',
  fontWeight: 600,
})
